import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { ArrowLeftIcon, Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline'
import { hideInitialLoaderImmediate } from '../../utils/initialLoaderManager'

/**
 * Shared layout component for legal pages (Privacy Policy, Terms of Service)
 * Provides consistent styling, navigation, and table of contents
 * Always renders in light mode for readability
 */
export default function LegalPageLayout({
  title,
  lastUpdated,
  sections = [],
  children
}) {
  const [activeSection, setActiveSection] = useState('')
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const { session } = useSelector((state) => state.auth)
  const navigate = useNavigate()

  // Hide initial loader immediately - legal pages don't need auth loading
  useEffect(() => {
    hideInitialLoaderImmediate()
  }, [])

  // Force light mode and fix scroll for legal pages
  useEffect(() => {
    const htmlElement = document.documentElement

    // Store current theme classes to restore on unmount
    const hadDark = htmlElement.classList.contains('dark')
    const hadPlatinum = htmlElement.classList.contains('platinum')
    const hadManual = htmlElement.classList.contains('dark-mode-manual')

    // Force light mode and enable normal document scrolling
    htmlElement.classList.remove('dark', 'platinum')
    htmlElement.classList.add('light', 'dark-mode-manual', 'legal-page')

    // Restore previous theme on unmount
    return () => {
      htmlElement.classList.remove('light', 'dark-mode-manual', 'legal-page')
      if (hadDark) htmlElement.classList.add('dark')
      if (hadPlatinum) htmlElement.classList.add('platinum')
      if (hadManual) htmlElement.classList.add('dark-mode-manual')
    }
  }, [])

  // Track scroll position to highlight active section in TOC
  useEffect(() => {
    const handleScroll = () => {
      const sectionElements = sections.map(s => document.getElementById(s.id))
      const scrollPosition = window.scrollY + 120

      for (let i = sectionElements.length - 1; i >= 0; i--) {
        const element = sectionElements[i]
        if (element && element.offsetTop <= scrollPosition) {
          setActiveSection(sections[i].id)
          break
        }
      }
    }

    window.addEventListener('scroll', handleScroll)
    handleScroll() // Initial check
    return () => window.removeEventListener('scroll', handleScroll)
  }, [sections])

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId)
    if (element) {
      const headerOffset = 80
      const elementPosition = element.getBoundingClientRect().top
      const offsetPosition = elementPosition + window.scrollY - headerOffset

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      })
    }
    setMobileMenuOpen(false)
  }

  const handleBack = () => {
    if (session) {
      navigate('/board')
    } else {
      navigate('/login')
    }
  }

  return (
    <div className="min-h-screen bg-[#fafafa] font-system">
      {/* Minimal Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-neutral-200/60">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            {/* Back button */}
            <button
              onClick={handleBack}
              className="flex items-center gap-1.5 text-neutral-500 hover:text-neutral-900 transition-colors text-sm"
            >
              <ArrowLeftIcon className="w-4 h-4" />
              <span className="hidden sm:inline">
                {session ? 'Back to App' : 'Back'}
              </span>
            </button>

            {/* Logo */}
            <img
              src="/taskrdo-logo.png"
              alt="taskrdo"
              className="h-10"
            />

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-1.5 text-neutral-500 hover:text-neutral-900 transition-colors"
            >
              {mobileMenuOpen ? (
                <XMarkIcon className="w-5 h-5" />
              ) : (
                <Bars3Icon className="w-5 h-5" />
              )}
            </button>

            {/* Desktop: empty spacer for alignment */}
            <div className="hidden lg:block w-20" />
          </div>
        </div>
      </header>

      {/* Mobile Table of Contents */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-white pt-14">
          <nav className="p-6 space-y-1 overflow-y-auto max-h-[calc(100vh-3.5rem)]">
            <p className="text-[11px] font-medium text-neutral-400 uppercase tracking-wider mb-3">
              Contents
            </p>
            {sections.map((section) => (
              <button
                type="button"
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className={`block w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                  activeSection === section.id
                    ? 'bg-neutral-100 text-neutral-900 font-medium'
                    : 'text-neutral-600 hover:text-neutral-900 hover:bg-neutral-50'
                }`}
              >
                {section.title}
              </button>
            ))}
          </nav>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-6 lg:px-8 py-12 lg:py-16">
        <div className="lg:grid lg:grid-cols-[1fr_220px] lg:gap-16">
          {/* Content Area */}
          <main className="min-w-0">
            {/* Page Header */}
            <header className="mb-12">
              <h1 className="text-[2.5rem] leading-tight font-semibold text-neutral-900 tracking-tight">
                {title}
              </h1>
              <p className="mt-3 text-sm text-neutral-500">
                Last updated {lastUpdated}
              </p>
            </header>

            {/* Page Content */}
            <article className="
              prose prose-neutral max-w-none
              prose-headings:font-semibold prose-headings:tracking-tight
              prose-h2:text-lg prose-h2:text-neutral-900 prose-h2:mt-14 prose-h2:mb-4 prose-h2:pb-3 prose-h2:border-b prose-h2:border-neutral-200
              prose-h3:text-base prose-h3:text-neutral-800 prose-h3:mt-8 prose-h3:mb-3
              prose-p:text-neutral-600 prose-p:leading-relaxed prose-p:text-[15px]
              prose-li:text-neutral-600 prose-li:text-[15px]
              prose-a:text-neutral-900 prose-a:font-medium prose-a:no-underline prose-a:border-b prose-a:border-neutral-300 hover:prose-a:border-neutral-900
              prose-strong:text-neutral-800 prose-strong:font-semibold
              prose-ul:my-4 prose-li:my-1
              [&>section]:pt-0 [&>section+section]:pt-10
              [&>section:nth-child(2)>h2:first-child]:mt-0
            ">
              {children}
            </article>
          </main>

          {/* Desktop Table of Contents */}
          <aside className="hidden lg:block">
            <div className="sticky top-20">
              <nav>
                <p className="text-[11px] font-medium text-neutral-400 uppercase tracking-wider mb-3">
                  On this page
                </p>
                <ul className="space-y-0.5">
                  {sections.map((section) => (
                    <li key={section.id}>
                      <button
                        type="button"
                        onClick={() => scrollToSection(section.id)}
                        className={`block w-full text-left py-1.5 text-[13px] transition-colors ${
                          activeSection === section.id
                            ? 'text-neutral-900 font-medium'
                            : 'text-neutral-500 hover:text-neutral-700'
                        }`}
                      >
                        {section.title}
                      </button>
                    </li>
                  ))}
                </ul>
              </nav>

            </div>
          </aside>
        </div>
      </div>

      {/* Minimal Footer */}
      <footer className="border-t border-neutral-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-neutral-400">
              {new Date().getFullYear()} Daniel Knight
            </p>
            <div className="flex items-center gap-6">
              <Link
                to="/privacy"
                className="text-xs text-neutral-500 hover:text-neutral-900 transition-colors"
              >
                Privacy
              </Link>
              <Link
                to="/terms"
                className="text-xs text-neutral-500 hover:text-neutral-900 transition-colors"
              >
                Terms
              </Link>
              <a
                href="mailto:support@taskrdo.com"
                className="text-xs text-neutral-500 hover:text-neutral-900 transition-colors"
              >
                Contact
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
